import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Logo from '../ui/Logo';
import { offices, email, whatsappNumbers } from '../../data/contact';

const FooterRoot = styled.footer`
  position: relative;
  background: linear-gradient(180deg, #07111f 0%, #030712 100%);
  color: #94a3b8;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #0e6b3c, #6dbb1a, #0e6b3c);
  }
`;

const CtaStrip = styled.div`
  position: relative;
  z-index: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  .inner {
    max-width: var(--container-width);
    margin: 0 auto;
    padding: 24px var(--container-padding);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }

  h3 {
    font-family: var(--font-display);
    font-size: 1.15rem;
    font-weight: 800;
    color: #fff;
    margin: 0;
  }

  p {
    font-size: 0.82rem;
    color: #94a3b8;
    margin: 2px 0 0;
  }
`;

const CtaBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  border-radius: 100px;
  background: linear-gradient(135deg, #0e6b3c, #6dbb1a);
  color: #fff;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.85rem;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    opacity: 0.95;
    transform: translateY(-1px);
  }
`;

const Main = styled.div`
  position: relative;
  z-index: 1;
  padding: 40px 0 28px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 0.85fr 0.85fr 1.15fr;
  gap: 28px 32px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr;
    gap: 28px;
  }

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const BrandText = styled.p`
  font-size: 0.82rem;
  line-height: 1.6;
  color: #94a3b8;
  margin: 12px 0 14px;
  max-width: 280px;
`;

const TrustBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;

  span {
    font-size: 0.68rem;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 100px;
    background: rgba(14, 107, 60, 0.12);
    border: 1px solid rgba(109, 187, 26, 0.2);
    color: #6dbb1a;
  }
`;

const ColTitle = styled.h4`
  font-family: var(--font-display);
  font-size: 0.88rem;
  font-weight: 700;
  color: #f8fafc;
  margin: 0 0 12px;
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  columns: 2;
  column-gap: 12px;

  @media (max-width: 580px) {
    columns: 1;
  }
`;

const FooterLink = styled(Link)`
  display: block;
  padding: 4px 0;
  font-size: 0.82rem;
  color: #94a3b8;
  text-decoration: none;
  break-inside: avoid;

  &:hover {
    color: #6dbb1a;
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 8px;
`;

const SocialBtn = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  font-size: 0.9rem;
  text-decoration: none;

  &:hover {
    background: #0e6b3c;
    color: #fff;
    border-color: transparent;
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContactLine = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
  color: #94a3b8;
  text-decoration: none;
  line-height: 1.4;

  i {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.8rem;
    background: rgba(255, 255, 255, 0.05);
    color: #6dbb1a;
  }

  &.wa i {
    color: #25d366;
    background: rgba(37, 211, 102, 0.12);
  }

  &:hover {
    color: #e2e8f0;
  }
`;

const OfficesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 10px;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`;

const OfficeMini = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);

  strong {
    display: block;
    font-family: var(--font-display);
    font-size: 0.72rem;
    font-weight: 700;
    color: #e2e8f0;
    margin-bottom: 4px;
  }

  p {
    margin: 0;
    font-size: 0.72rem;
    line-height: 1.45;
    color: #94a3b8;
  }

  a {
    display: inline-block;
    margin-top: 4px;
    font-size: 0.72rem;
    font-weight: 600;
    color: #6dbb1a;
    text-decoration: none;

    &:hover {
      color: #f2b705;
    }
  }
`;

const Bottom = styled.div`
  position: relative;
  z-index: 1;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  padding: 14px 0;

  .inner {
    max-width: var(--container-width);
    margin: 0 auto;
    padding: 0 var(--container-padding);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    font-size: 0.78rem;
    color: #64748b;
  }

  .legal {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;

    a {
      color: #64748b;
      text-decoration: none;
      &:hover {
        color: #6dbb1a;
      }
    }
  }
`;

const BackTop = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: #94a3b8;
  font-size: 0.75rem;
  font-family: var(--font-display);
  font-weight: 600;
  cursor: pointer;

  &:hover {
    color: #6dbb1a;
  }
`;

const quickLinks = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/projects', label: 'Portfolio' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/contact', label: 'Contact' },
];

const serviceLinks = [
  { to: '/lms', label: 'LMS Platform' },
  { to: '/projects', label: 'POS SaaS' },
  { to: '/services', label: 'Web Development' },
  { to: '/services', label: 'E-commerce' },
  { to: '/services', label: 'CRM & ERP' },
];

const footerOffices = offices.map((o) => ({
  ...o,
  shortLines:
    o.id === 'india'
      ? ['Vijayawada, AP, India']
      : ['Norwich, NR3', 'United Kingdom'],
}));

const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

const Footer = () => (
  <FooterRoot>
    <CtaStrip>
      <div className="inner">
        <div>
          <h3>Ready To Grow Your Business?</h3>
          <p>Free consultation · Reply within 24 hours</p>
        </div>
        <CtaBtn to="/contact">
          Get Free Consultation <i className="fas fa-arrow-right" />
        </CtaBtn>
      </div>
    </CtaStrip>

    <Main>
      <div className="container">
        <Grid>
          <div>
            <Logo variant="footer" />
            <BrandText>
              Premium websites, CRM, ERP & custom software for businesses in India and the UK.
            </BrandText>
            <TrustBadges>
              <span>50+ Projects</span>
              <span>30+ Clients</span>
              <span>24/7 Support</span>
            </TrustBadges>
            <SocialRow>
              <SocialBtn href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <i className="fab fa-facebook-f" />
              </SocialBtn>
              <SocialBtn href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <i className="fab fa-twitter" />
              </SocialBtn>
              <SocialBtn href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <i className="fab fa-linkedin-in" />
              </SocialBtn>
              <SocialBtn href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <i className="fab fa-instagram" />
              </SocialBtn>
            </SocialRow>
          </div>

          <div>
            <ColTitle>Quick Links</ColTitle>
            <LinkList>
              {quickLinks.map((l) => (
                <li key={l.label}>
                  <FooterLink to={l.to}>{l.label}</FooterLink>
                </li>
              ))}
            </LinkList>
          </div>

          <div>
            <ColTitle>Services</ColTitle>
            <LinkList>
              {serviceLinks.map((l) => (
                <li key={l.label}>
                  <FooterLink to={l.to}>{l.label}</FooterLink>
                </li>
              ))}
            </LinkList>
          </div>

          <div>
            <ColTitle>Contact Us</ColTitle>
            <ContactList>
              <ContactLine href={`mailto:${email}`}>
                <i className="fas fa-envelope" />
                <span>{email}</span>
              </ContactLine>
              {whatsappNumbers.map((w) => (
                <ContactLine
                  key={w.id}
                  className="wa"
                  href={w.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-whatsapp" />
                  <span>
                    {w.label}: {w.display}
                  </span>
                </ContactLine>
              ))}
            </ContactList>
            <OfficesGrid>
              {footerOffices.map((office) => (
                <OfficeMini key={office.id}>
                  <strong>{office.id === 'india' ? 'India' : 'UK'}</strong>
                  {office.shortLines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                  <a href={office.phoneHref}>{office.phone}</a>
                </OfficeMini>
              ))}
            </OfficesGrid>
          </div>
        </Grid>
      </div>
    </Main>

    <Bottom>
      <div className="inner">
        <span>© {new Date().getFullYear()} Rhutech Solutions. All Rights Reserved.</span>
        <div className="legal">
          <Link to="/contact">Privacy</Link>
          <Link to="/contact">Terms</Link>
          <Link to="/contact">Get Quote</Link>
        </div>
        <BackTop type="button" onClick={scrollTop} aria-label="Back to top">
          <i className="fas fa-arrow-up" /> Top
        </BackTop>
      </div>
    </Bottom>
  </FooterRoot>
);

export default Footer;
